import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard,
  Ticket,
  PenSquare,
  LogOut,
  Menu,
  X,
  Receipt,
  Film,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePortalScope } from '../../context/PortalScopeContext';
import { api } from '../../lib/api';
import logo from '../../assets/logo.png';
import ImpersonationBanner from '../../components/portal/ImpersonationBanner';
import ThemeToggle from '../../components/ui/ThemeToggle';
import { useConsoleTheme } from '../../lib/theme';

export default function DashboardLayout() {
  const { user, logout, impersonator } = useAuth();
  const { account } = usePortalScope();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unsignedCount, setUnsignedCount] = useState(0);
  useConsoleTheme(); // marks <html> with the console theme while mounted

  // Sprint J: synthesized "admin preview" replaced with real impersonation
  // (a server-issued client session with impersonator_user_id set). The
  // portal renders the principal's view 1:1; the banner above signals the
  // active impersonation.
  const basePath = '/portal';
  const showInvoices = user?.billingAccess === true;

  useEffect(() => {
    if (!showInvoices) return;
    let alive = true;
    api.get('/api/client/invoices?page=1&page_size=1')
      .then((d) => { if (alive) setUnsignedCount(d?.signature_required_unsigned_count || 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, [showInvoices]);

  // Sprint D item 3.5 collapsed the standalone /portal/audits + /portal/domains
  // tabs — those signals now surface as Overview cards (item 3.6).
  const navItems = [
    { to: `${basePath}/overview`, label: 'Overview', icon: LayoutDashboard },
    showInvoices && { to: `${basePath}/invoices`, label: 'Invoices', icon: Receipt, badge: unsignedCount > 0 },
    { to: `${basePath}/tickets`, label: 'Tickets', icon: Ticket },
    // Sprint I D3: SOON flag removed — Visual Editor route is functional.
    { to: `${basePath}/edits`, label: 'Content Edits', icon: PenSquare },
    // Media tab shows for EVERY school. Without a media package the route
    // renders MediaPackageInfo (a free explainer + contact CTA); once a media
    // invoice is signed the same route becomes the live booking calendar.
    { to: `${basePath}/media`, label: 'Media', icon: Film },
    // The analytics dashboard is a separate application on its own origin.
    // This link is the sign-in-once handoff: the endpoint mints a short-lived
    // token and posts it to the dashboard, which keeps its own session.
    { href: '/api/sso/analytics', label: 'Analytics', icon: BarChart3 },
  ].filter(Boolean);

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="portal-skin min-h-screen relative">
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 grain-bg opacity-[0.13]" />
      </div>

      <ImpersonationBanner />
      <div className="relative z-[1] flex min-h-screen">
        <Sidebar navItems={navItems} account={account} isPreview={!!impersonator} />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="flex items-center justify-between px-4 md:px-8 py-4 border-b border-white/[0.05] bg-[#0a0a0f]/60 backdrop-blur-md sticky top-0 z-20">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                aria-label="Open navigation"
              >
                <Menu className="w-4 h-4" />
              </button>
              <div className="md:hidden flex items-center gap-2">
                <img src={logo} alt="NexSav" data-light-invert className="h-6 w-auto opacity-90" />
                <span className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">
                  {impersonator ? 'Preview' : 'Portal'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <div className="text-sm text-white">{user?.name}</div>
                <div className="text-[11px] text-gray-500">{user?.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <button
                  onClick={onLogout}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-[13px] text-gray-300 hover:text-white hover:border-white/[0.15] transition-all"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-4 md:px-8 py-8 md:py-10">
            <Outlet />
          </main>
        </div>
      </div>

      <MobileSidebar
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        navItems={navItems}
        account={account}
        isPreview={!!impersonator}
      />
    </div>
  );
}

function Sidebar({ navItems, account, isPreview }) {
  return (
    <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-white/[0.05] bg-[#0a0a0f]/50 backdrop-blur-md">
      <div className="px-5 py-5 border-b border-white/[0.05]">
        <Link to="/" className="flex items-center gap-3 group">
          <img src={logo} alt="NexSav" data-light-invert className="h-7 w-auto opacity-90 group-hover:opacity-100 transition-opacity" />
          <div>
            <div className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">
              Client Portal
            </div>
            {account?.name && (
              <div className="text-[11px] text-gray-400 truncate max-w-[160px]">{account.name}</div>
            )}
          </div>
        </Link>
      </div>
      <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5">
        {navItems.map((item) => (
          <NavLinkItem key={item.to || item.href} item={item} />
        ))}
      </nav>
      <div className="px-5 py-4 border-t border-white/[0.05]">
        {isPreview ? (
          <Link to="/admin/accounts" className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors">
            ← Back to admin
          </Link>
        ) : (
          <Link to="/" className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors">
            ← Back to nexsav.net
          </Link>
        )}
      </div>
    </aside>
  );
}

function MobileSidebar({ open, onClose, navItems, account, isPreview }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 md:hidden"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative w-72 h-full bg-[#0a0a0f] border-r border-white/[0.08] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-white/[0.05]">
              <Link to="/" onClick={onClose} className="flex items-center gap-3">
                <img src={logo} alt="NexSav" data-light-invert className="h-7 w-auto opacity-90" />
                <div>
                  <div className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">Portal</div>
                  {account?.name && <div className="text-[11px] text-gray-400 truncate max-w-[140px]">{account.name}</div>}
                </div>
              </Link>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5" onClick={onClose}>
              {navItems.map((item) => (
                <NavLinkItem key={item.to || item.href} item={item} />
              ))}
            </nav>
            {isPreview && (
              <div className="px-5 py-4 border-t border-white/[0.05]">
                <Link to="/admin/accounts" className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors">
                  ← Back to admin
                </Link>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function NavLinkItem({ item }) {
  const { icon: Icon, label, to, href, soon, badge } = item;
  if (href) {
    return (
      <a
        href={href}
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-gray-400 hover:text-white hover:bg-white/[0.03] border border-transparent"
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1">{label}</span>
      </a>
    );
  }
  if (soon) {
    return (
      <NavLink
        to={to}
        end
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
            isActive
              ? 'bg-white/[0.05] text-white border border-white/[0.06]'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] border border-transparent'
          }`
        }
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1">{label}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-gray-500 tracking-wider uppercase">
          Soon
        </span>
      </NavLink>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
          isActive
            ? 'bg-gradient-to-r from-[#B0FC38]/[0.12] to-transparent text-white border border-[#B0FC38]/20'
            : 'text-gray-400 hover:text-white hover:bg-white/[0.03] border border-transparent'
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {badge && (
        <span data-testid={`nav-badge-${label.toLowerCase()}`} className="w-2 h-2 rounded-full bg-red-400 shadow shadow-red-500/40" />
      )}
    </NavLink>
  );
}
